import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
    Subject,
    Observable,
    takeUntil,
    switchMap,
    tap,
    map,
    of,
    filter,
    take,
} from 'rxjs';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { StepValidationResult } from '../../../../shared/models/step-validation-result';
import { FormGroup } from '@angular/forms';
import { ApplicationState } from '../../../../../../core/models/application-state/application-state';
import { ValidationResult } from '../../../../../../core/services/validation/models/validation-result';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { FormEntryService } from '../../../../../../core/services/form-entry/form-entry.service';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { StepLink } from '../../../../shared/models/step-link';
import { ApplicationValidationHelper } from '../../../../shared/helpers/application-validator/application-validator.helper';
import {
    FrnFilingRoutes,
    RouteConstants,
} from '../../../../../../core/constants/routes';
import { DirectEntryStepPaths } from '../../../../direct-entry/shared/constants/direct-entry-steps';
import { FrnFilingService } from '../../services/frn-filing.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../../../shared/components/modal/modal.component';
import { CheckmarkDialogComponent } from '../../../../../../shared/components/checkmark-dialog/checkmark-dialog.component';
import {
    SubmissionToEwaCompleteDialogMessage,
    FrnFilingHandoverToEwaIncompleteDialogMessage,
} from '../../../../shared/constants/submission-dialog-message';
import { hasOpenDialogs } from '../../../../../../core/functions/dialog-helper';
import { FocusHelper } from '../../../../../../core/helpers/focus/focus-helper';
import { ReturnRouteService } from '../../../../../../core/services/navigation/return-route.service';

@Component({
    selector: 'app-frn-filing-page-base',
    changeDetection: ChangeDetectionStrategy.Eager,
    template:
        '<!-- This component serves as a base and does not render any content -->',
})
export abstract class FrnFilingPageBaseComponent<
    TRequest,
    TResponse,
    TFormModel,
>
    implements OnInit, OnDestroy
{
    protected destroy$ = new Subject<void>();
    private readonly hostElementRef = inject(ElementRef<HTMLElement>);

    abstract form:
        | FormGroup<FormGroupType<TFormModel>>
        | FormGroup<NestedFormGroupType<TFormModel>>;

    applicationState: ApplicationState | null =
        this.applicationStateService.getCurrentApplicationState();
    validationResult: ValidationResult | null = null;
    hasChanges: boolean = false;
    applicationId: string | undefined;

    /* eslint-disable @angular-eslint/prefer-inject */
    protected constructor(
        protected readonly route: ActivatedRoute,
        protected readonly stepService: StepNavigationService,
        protected readonly router: Router,
        protected readonly formEntryService: FormEntryService,
        protected readonly applicationStateService: ApplicationStateService,
        protected readonly applicationFormControlManagerService: ApplicationFormControlManagerService,
        protected readonly frnFilingService: FrnFilingService,
        protected readonly dialog: MatDialog,
        protected readonly returnRouteService: ReturnRouteService,
    ) {}
    /* eslint-enable @angular-eslint/prefer-inject */

    ngOnInit(): void {
        this.loadForm()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.setupReadOnly();

                this.formEntryService.handleFormDirtying<TFormModel>(
                    this.form,
                    this.destroy$,
                    (newValue: boolean) => {
                        // Don't track changes if the form is read-only
                        if (!this.isReadOnly()) {
                            this.hasChanges = newValue;
                        }
                    },
                );

                this.setupReadOnly();
                this.loadAndSetStepValidations();
                this.handleNextClick();
                this.handleSaveAndExitClick();
                this.handlePreviousClick();
                this.handleHandoverToEwaClick();
                this.handleProceedToStep();
                this.postFormBindingActions();
                this.focusFirstField();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected loadForm(): Observable<void> {
        return this.route.paramMap.pipe(
            switchMap((params: ParamMap) => {
                const applicationId = params.get('id');
                this.applicationId = applicationId ?? undefined;

                // If no applicationId, do not call API to load form data
                if (!applicationId) {
                    return of(void 0);
                }

                return this.getFormDataFromApi(applicationId).pipe(
                    tap((response: TResponse) => {
                        this.onLoad(response);
                    }),
                    map(() => void 0),
                );
            }),
        );
    }

    private onLoad(response: TResponse): void {
        this.bindForm(response);

        const currentStep: StepLink | undefined =
            this.stepService.getCurrentStep();

        if (currentStep?.passedValidation === undefined) {
            return;
        }

        ApplicationValidationHelper.markAllRequiredAsTouched(this.form);
    }

    private handleAction<T extends StepLink | void>(
        action$: Observable<T>,
        onSuccess: (value: T) => void,
    ): void {
        action$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((value: T) => {
                    //  If no changes or read-only, don't save
                    if (!this.hasChanges || this.isReadOnly()) {
                        // If there are no changes, we don't need to save the data
                        // but we still want to proceed with the `onSuccess` action.
                        return of(value);
                    }

                    const request = this.generateRequestObject();
                    return this.saveFormDataToApi(request).pipe(
                        tap((stepValResult: StepValidationResult | null) => {
                            // While most steps include a validation result which gets displayed in the step menu, there could be
                            // some steps that do not require or support validations. In such cases, the validation result will be null
                            // and this check will accommodate for that.
                            if (stepValResult) {
                                this.stepService.setCurrentStepValidation(
                                    stepValResult.passedFinalValidation,
                                );
                            }
                        }),
                        switchMap(() => {
                            /**
                             * Post-save actions specific to the page can be handled here. The pipeline will wait for these actions to complete
                             * before proceeding to the next steps in the case that any post-save actions are asynchronous.
                             */
                            return this.postSaveActions();
                        }),
                        tap(() => {
                            // Setting the flag to "false" allows the page navigation to proceed and not be blocked by the unsaved changes.
                            // See: BlockNavigationIfChangeGuard logic applied on all application pages.
                            this.hasChanges = false;
                        }),
                        map(() => value), // Pass through the original value
                    );
                }),
            )
            .subscribe({
                next: (value) => {
                    onSuccess(value);
                },
            });
    }

    /**
     * Determines if the current application is linked to a direct entry application.
     * @returns True if the application is created from direct entry, false otherwise.
     */
    protected isFrnFilingLinkedToDirectEntryApplication(): boolean {
        return this.applicationState?.hasFrnRequest === true;
    }

    /**
     * Determines if the FRN filing is in read-only mode.
     *
     * @return true if the FRN filing is read-only; otherwise, false.
     */
    protected isReadOnly(): boolean {
        if (!this.applicationId) {
            return false;
        }

        return this.applicationFormControlManagerService.isReadOnly();
    }

    /**
     * Initializes and enforces read-only behavior for the current FRN filing context.
     *
     * When the current FRN filing is marked as read-only:
     * - Disables the root FormGroup (cascading to all child controls) without emitting value change events.
     * - Resets hasChanges to prevent save operations, navigation guards, or step transitions from triggering.
     *
     * This method should be invoked once during initialization since the read-only state
     * does not change during the lifetime of the page.
     */
    private setupReadOnly(): void {
        if (this.isReadOnly()) {
            this.form.disable({ emitEvent: false });
            this.hasChanges = false;
        }
    }

    protected handleNextClick(): void {
        const guardedNextClick$ = this.stepService.nextClick$.pipe(
            tap(() => {
                ApplicationValidationHelper.markAllRequiredAsTouched(this.form);
            }),

            // Only continue if the form is valid or existing
            filter(
                () =>
                    this.form.valid ||
                    FrnFilingRoutes.isExistingFrnFilingRoute(this.router.url),
            ),
        );

        this.handleAction(guardedNextClick$, () => {
            this.stepService.goToNextStep();
        });
    }

    protected handleSaveAndExitClick(): void {
        this.handleAction(this.stepService.saveAndClose$, () => {
            // Navigate back to Applicant Info page if linked to direct entry
            if (
                this.isFrnFilingLinkedToDirectEntryApplication() &&
                this.applicationId
            ) {
                this.validateAndSyncToDirectEntry()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        this.router.navigate([
                            RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                                DirectEntryStepPaths.applicantInfo,
                                this.applicationId!,
                            ),
                        ]);
                    });
            } else {
                const returnRoute = this.returnRouteService.consume();
                if (returnRoute) {
                    this.router.navigate([returnRoute]);
                } else {
                    this.router.navigate([
                        RouteConstants.Dashboard.getFullRoute('INDEX'),
                    ]);
                }
            }
        });
    }

    protected handlePreviousClick(): void {
        this.handleAction(this.stepService.previousClick$, () => {
            this.stepService.goToPreviousStep();
        });
    }

    protected handleHandoverToEwaClick(): void {
        this.handleAction(this.stepService.handoverToEwa$, () => {
            this.handoverToEwaAction();
        });
    }

    private handleProceedToStep(): void {
        this.handleAction(
            this.stepService.proceedingToStep$,
            (stepLink: StepLink) => {
                this.router.navigate([stepLink.url]);
            },
        );
    }

    private loadAndSetStepValidations(): void {
        if (!this.applicationId) {
            return;
        }

        this.frnFilingService
            .getPreviouslySavedStepValidations(this.applicationId)
            .pipe(
                takeUntil(this.destroy$),
                tap((validations) => {
                    this.stepService.setStepValidations(validations, false);
                }),
            )
            .subscribe();
    }

    /**
     * Logic to bind the response data to the page's form.
     * @param response
     */
    protected abstract bindForm(response: TResponse): void;

    /**
     * API call to get the form data for the page.
     * @param applicationId The application's Id.
     */
    protected abstract getFormDataFromApi(
        applicationId: string,
    ): Observable<TResponse>;

    /**
     * API call to save the form data to the server.
     * @param request
     */
    protected abstract saveFormDataToApi(
        request: TRequest,
    ): Observable<StepValidationResult | null>;

    /**
     * Generates the request object (from the form) in preparate to send the data to the server during saving actions.
     */
    protected abstract generateRequestObject(): TRequest;

    /**
     * Handles the handover to EWA action for both Frn Filing Type and Detail. Checks if all steps have passed validation,
     * If not all steps pass, shows a warning dialog. If all pass or user confirms,
     * calls the service to handover to EWA and shows success dialog.
     */
    private handoverToEwaAction(): void {
        if (!this.applicationId) {
            throw new Error('Application ID cannot be null');
        }

        // Check if all steps have passed validation
        this.stepService.steps$
            .pipe(
                take(1),
                switchMap((steps: StepLink[]) => {
                    // If there are no steps, don't proceed
                    if (steps.length === 0) {
                        return of(void 0);
                    }

                    const allStepsPassed = steps.every(
                        (step) => step.passedValidation,
                    );

                    if (!allStepsPassed) {
                        // Show warning dialog if not all steps have passed
                        return this.showIncompleteFrnFilingWarningDialog().pipe(
                            switchMap((proceed: boolean) => {
                                if (proceed) {
                                    return this.performHandoverToEwa(
                                        allStepsPassed,
                                    );
                                }
                                return of(void 0);
                            }),
                        );
                    } else {
                        // All steps passed, proceed directly
                        return this.performHandoverToEwa(allStepsPassed);
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    /**
     * Validates all FRN filing steps and syncs to Direct Entry application if all steps pass validation.
     * @returns Observable that completes when validation and sync (if needed) are done.
     */
    private validateAndSyncToDirectEntry(): Observable<void> {
        if (!this.applicationId) {
            return of(void 0);
        }

        return this.frnFilingService.validateAllSteps(this.applicationId).pipe(
            tap((validations) => {
                this.stepService.setStepValidations(validations, false);
            }),
            switchMap((validations) => {
                const allStepsPassed = validations.every(
                    (validation) => validation.passedFinalValidation,
                );

                // Only sync if all steps are valid
                if (allStepsPassed) {
                    return this.frnFilingService
                        .syncToDirectEntry(this.applicationId!)
                        .pipe(
                            tap((response) => {
                                if (!response.success) {
                                    console.error(
                                        'Sync to Direct Entry failed:',
                                        response.message,
                                    );
                                }
                            }),
                            map(() => void 0),
                        );
                }

                // If all steps aren't valid, navigate without syncing
                return of(void 0);
            }),
        );
    }

    /**
     * Performs the actual handover to EWA API call and shows success dialog.
     * @param allStepsPassed Whether all steps have passed validation.
     */
    private performHandoverToEwa(allStepsPassed: boolean): Observable<void> {
        if (!this.applicationId) {
            return of(void 0);
        }

        return this.frnFilingService
            .submitToEwa(this.applicationId, {
                skipValidations: !allStepsPassed,
            })
            .pipe(
                tap(() => {
                    this.showSubmissionSuccessDialog();
                }),
                map(() => void 0),
            );
    }

    /**
     * Shows a warning dialog when all the required fields are not complete.
     * @returns Observable that emits true if user wants to proceed, false otherwise.
     */
    private showIncompleteFrnFilingWarningDialog(): Observable<boolean> {
        if (hasOpenDialogs(this.dialog)) {
            return of(false);
        }

        const dialogRef = this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: FrnFilingHandoverToEwaIncompleteDialogMessage.modalTitle,
                content: FrnFilingHandoverToEwaIncompleteDialogMessage.content,
                primaryAction: {
                    title: FrnFilingHandoverToEwaIncompleteDialogMessage.primaryActionTitle,
                    action: () => dialogRef.close(true),
                },
                cancelTitle:
                    FrnFilingHandoverToEwaIncompleteDialogMessage.cancelTitle,
            },
        });

        return dialogRef.afterClosed();
    }

    /**
     * Shows the submission success dialog and navigates to dashboard.
     */
    private showSubmissionSuccessDialog(): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        const dialogRef = this.dialog.open(CheckmarkDialogComponent, {
            disableClose: true,
            data: {
                title: SubmissionToEwaCompleteDialogMessage.modalTitle,
                content: SubmissionToEwaCompleteDialogMessage.modalContent,
                primaryAction: {
                    title: SubmissionToEwaCompleteDialogMessage.primaryActionTitle,
                },
            },
        });

        dialogRef.afterClosed().subscribe(() => {
            this.router.navigate([
                RouteConstants.Dashboard.getFullRoute('INDEX'),
            ]);
        });
    }

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle any extra actions that are specific to the page that
     * need to occur AFTER the form is initially loaded.
     */
    protected postFormBindingActions(): void {}

    /**
     * Moves keyboard focus to the first field on the page after it loads. This ensures that
     * regardless of how the user navigated to the page (Next, Previous, or the Step Menu), their
     * focus starts on the first field.
     */
    protected focusFirstField(): void {
        if (!this.shouldAutoFocusFirstField()) {
            return;
        }

        FocusHelper.deferFocusFirstField(this.hostElementRef.nativeElement);
    }

    /**
     * (OPTIONAL) Override in a child class to opt out of automatically focusing the first field on load.
     * This is useful for pages that manage their own focus (e.g. multi-step sub-wizards).
     */
    protected shouldAutoFocusFirstField(): boolean {
        return true;
    }

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle any extra actions that are specific to the page that
     * need to occur AFTER the form is successfully saved. By default, it returns an Observable that completes immediately.
     *
     * @return An Observable that completes when post-save actions are done.
     */
    protected postSaveActions(): Observable<void> {
        return of(void 0);
    }
}
